import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Reveal, Lines, EASE } from "../components/site/Motion";

export default function WithdrawalPage() {
  const [form, setForm] = useState({ reservation_id: "", email: "", reason: "" });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Recedere dal contratto — SWAPA";
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/recesso", form);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full border border-white/15 bg-ink px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 focus:border-swapa-yellow focus:outline-none";

  return (
    <div data-testid="withdrawal-page" className="bg-ink pb-28 pt-32 sm:pt-40">
      <div className="mx-auto max-w-2xl px-5 sm:px-10">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-swapa-yellow">Diritto di recesso</p>
        <h1 className="mt-6 font-display font-black uppercase leading-[0.9] tracking-tighter">
          <Lines animate="load" lines={["Recedere dal", "contratto qui."]} className="text-4xl text-white sm:text-6xl" />
        </h1>
        <Reveal delay={0.25}>
          <p className="mt-6 text-sm leading-relaxed text-zinc-400">
            Puoi cancellare la tua prenotazione online di €200 in qualsiasi momento prima della
            sottoscrizione del contratto definitivo di acquisto, senza penali. In caso di recesso
            esercitato validamente, il rimborso dei pagamenti ricevuti avviene entro 14 giorni da
            quando veniamo informati della tua decisione, secondo il Codice del Consumo.
          </p>
        </Reveal>

        {done ? (
          <motion.div
            data-testid="withdrawal-success"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mt-12 border border-swapa-yellow/40 bg-card p-10"
          >
            <span className="flex h-12 w-12 items-center justify-center bg-swapa-yellow"><Check className="h-6 w-6 text-black" /></span>
            <h2 className="mt-6 font-display text-2xl font-extrabold uppercase tracking-tight text-white">Richiesta registrata.</h2>
            <p className="mt-4 text-sm leading-relaxed text-zinc-400">
              La tua richiesta di recesso è stata registrata. Riceverai conferma via email e il
              rimborso sarà elaborato secondo i termini previsti. Per urgenze: info@swapa.it — 800 826 557.
            </p>
          </motion.div>
        ) : (
          <Reveal delay={0.3}>
            <form onSubmit={submit} data-testid="withdrawal-form" className="mt-12 border border-white/10 bg-card p-6 sm:p-10">
              <div className="grid gap-4">
                <label className="block">
                  <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">Numero prenotazione *</span>
                  <input data-testid="withdrawal-reservation-input" required value={form.reservation_id} onChange={(e) => setForm({ ...form, reservation_id: e.target.value })} placeholder="ZIP-XXXXXXXX" className={inputCls} />
                </label>
                <label className="block">
                  <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">Email utilizzata per la prenotazione *</span>
                  <input data-testid="withdrawal-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="nome@email.it" className={inputCls} />
                </label>
                <label className="block">
                  <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">Motivo (facoltativo)</span>
                  <textarea data-testid="withdrawal-reason-input" rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Facoltativo" className={inputCls} />
                </label>
              </div>
              {error && <p data-testid="withdrawal-error" className="mt-4 text-sm text-red-400">{error}</p>}
              <button
                type="submit"
                data-testid="withdrawal-submit-button"
                disabled={loading}
                className="mt-8 w-full bg-swapa-yellow py-4 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-black transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-50"
              >
                {loading ? "Invio in corso…" : "Conferma recesso e richiedi rimborso"}
              </button>
              <p className="mt-4 text-xs leading-relaxed text-zinc-600">
                La richiesta vale come manifestazione di volontà di recedere dalla prenotazione online.
                Restano impregiudicati i diritti inderogabili del consumatore previsti dalla normativa vigente.
              </p>
            </form>
          </Reveal>
        )}
      </div>
    </div>
  );
}
